import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { QueryBuilderField } from "src/ui/shared/form/QueryBuilderField/QueryBuilderField";
import { HoverRow } from "src/ui/shared/table/HoverRow";
import { makeStyles } from "tss-react/mui";

import { AnnouncementSubscriber } from "@admin/api/announcements";
import { useExportAnnouncementEmails } from "@admin/hooks/useExportAnnouncementEmails";
import { useFetchSubscribers } from "@admin/hooks/useFetchSubscribers";
import { useUnsubscribeSubscribers } from "@admin/hooks/useUnsubscribeSubscribers";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { DropdownItem } from "@devices/components/shared";
import { ConfirmDialog } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    DialogContentText,
    Paper,
    TableContainer,
    TableRow,
    Typography,
} from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import {
    getAnnouncementsTableColumns,
    invalidEmailPreviewLimit,
    isEmailInTextarea,
    parseEmailList,
    removeProcessedEmailsFromTextarea,
    toggleEmailInTextarea,
    validateEmailList,
} from "./AdminAnnouncementsPage.utils";

const reasonMaxLength = 200;
const defaultPageSize = 10;
const emailsTextareaMinRows = 3;

const useStyles = makeStyles()((theme) => ({
    card: { marginBottom: theme.spacing(5) },
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    searchContainer: {
        flex: 1,
        minWidth: 240,
    },
    toolbarActions: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    countText: {
        marginBottom: theme.spacing(2),
    },
    formBox: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0),
    },
    formActions: {
        display: "flex",
        justifyContent: "flex-end",
    },
    alert: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
}));

const AdminAnnouncementsPage = () => {
    const [emailFilter, setEmailFilter] = React.useState("");
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "mail", desc: false }]);

    const [emailsText, setEmailsText] = React.useState("");
    const [reason, setReason] = React.useState("");
    const [formError, setFormError] = React.useState<string | null>(null);
    const [successResult, setSuccessResult] = React.useState<{ removed: string[]; unknown: string[] } | null>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [pendingEmails, setPendingEmails] = React.useState<string[]>([]);
    const [pendingReason, setPendingReason] = React.useState<string | undefined>();
    const exportAnchorRef = React.useRef<HTMLButtonElement | null>(null);

    const currentSort = sorting[0] ?? { id: "mail", desc: false };

    const fetchPayload = {
        email: emailFilter || undefined,
        pageIndex: pageIndex + pageIndexOffset,
        pageSize,
        sortCriteria: currentSort.id as "mail" | "source",
        sortDirection: (currentSort.desc ? 1 : 0) as 0 | 1,
    };

    const { data, isLoading, refetch } = useFetchSubscribers(fetchPayload);
    const { isLoading: isExporting, onExportSubscribers, onExportCustomers } = useExportAnnouncementEmails();
    const { isLoading: isUnsubscribing, onUnsubscribe } = useUnsubscribeSubscribers();

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const announcementSubscribers = data?.list ?? [];
    const totalCount = data?.count ?? 0;

    const tableColumns = getAnnouncementsTableColumns(t);

    const table = useReactTable<AnnouncementSubscriber>({
        data: announcementSubscribers,
        columns: tableColumns,
        state: { sorting },
        onSortingChange: (updater) => {
            setSorting(updater);
            setPageIndex(0);
        },
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const emptyMessage = data?.message || t("pages:adminAnnouncementsPage.emptyState");

    const handleEmailSearch = (value: string) => {
        setEmailFilter(value);
        setPageIndex(0);
    };

    const handleExportSubscribers = async () => {
        const success = await onExportSubscribers();
        if (success) {
            showSuccessToast(t("pages:adminAnnouncementsPage.export.subscribersSuccess"));
        }
    };

    const handleExportCustomers = async () => {
        const success = await onExportCustomers();
        if (success) {
            showSuccessToast(t("pages:adminAnnouncementsPage.export.customersSuccess"));
        }
    };

    const handleToggleRow = (mail: string) => {
        setFormError(null);
        setSuccessResult(null);
        setEmailsText((prev) => toggleEmailInTextarea(prev, mail));
    };

    const handleUnsubscribeClick = () => {
        setFormError(null);
        setSuccessResult(null);

        const parsedEmails = parseEmailList(emailsText);
        if (parsedEmails.length === 0) {
            setFormError(t("pages:adminAnnouncementsPage.unsubscribe.validation.emailsRequired"));
            return;
        }

        const { valid, invalid } = validateEmailList(parsedEmails);
        if (invalid.length > 0) {
            const shown = invalid.slice(0, invalidEmailPreviewLimit);
            const restCount = invalid.length - shown.length;
            let message = t("pages:adminAnnouncementsPage.unsubscribe.validation.invalidEmails", {
                count: invalid.length,
                addresses: shown.join(", "),
            });

            if (restCount > 0) {
                message += ` ${t("pages:adminAnnouncementsPage.unsubscribe.validation.invalidEmailsMore", { count: restCount })}`;
            }

            setFormError(message);
            return;
        }

        if (reason.length > reasonMaxLength) {
            setFormError(t("pages:adminAnnouncementsPage.unsubscribe.validation.reasonTooLong"));
            return;
        }

        setPendingEmails(valid);
        setPendingReason(reason.trim() || undefined);
        setConfirmOpen(true);
    };

    const handleConfirmUnsubscribe = async () => {
        setConfirmOpen(false);
        setFormError(null);
        setSuccessResult(null);

        try {
            const result = await onUnsubscribe({
                emails: pendingEmails,
                reason: pendingReason,
            });
            setSuccessResult(result);
            setEmailsText((prev) => removeProcessedEmailsFromTextarea(prev, result.removed));

            if (result.removed.length === 0) {
                if (result.unknown.length > 0) {
                    setFormError(
                        t("pages:adminAnnouncementsPage.unsubscribe.unknown", {
                            addresses: result.unknown.join(", "),
                        }),
                    );
                } else {
                    setFormError(t("pages:adminAnnouncementsPage.unsubscribe.noneRemoved"));
                }
                return;
            }

            showSuccessToast(t("pages:adminAnnouncementsPage.unsubscribe.success", { count: result.removed.length }));
            refetch();
        } catch (error) {
            const err = error as Error;
            setFormError(err.message);
        }
    };

    return (
        <PageContainer>
            <PageHeader
                title={t("pages:adminAnnouncementsPage.title")}
                description={t("pages:adminAnnouncementsPage.description")}
            />

            <Card className={classes.card}>
                <CardHeader title={<Typography variant="h6">{t("pages:adminAnnouncementsPage.unsubscribe.title")}</Typography>} />
                <CardContent>
                    {formError ? (
                        <CustomAlert severity="error" className={classes.alert}>
                            {formError}
                        </CustomAlert>
                    ) : null}

                    {successResult?.unknown.length ? (
                        <CustomAlert severity="warning" className={classes.alert}>
                            {t("pages:adminAnnouncementsPage.unsubscribe.unknown", {
                                addresses: successResult.unknown.join(", "),
                            })}
                        </CustomAlert>
                    ) : null}

                    <Box className={classes.formBox}>
                        <UncontrolledTextField
                            name="unsubscribe-emails"
                            label={t("pages:adminAnnouncementsPage.unsubscribe.emails.label")}
                            placeholder={t("pages:adminAnnouncementsPage.unsubscribe.emails.placeholder")}
                            helperText={t("pages:adminAnnouncementsPage.unsubscribe.emails.helper")}
                            value={emailsText}
                            onChange={(event) => {
                                setFormError(null);
                                setSuccessResult(null);
                                setEmailsText(event.target.value);
                            }}
                            multiline
                            minRows={emailsTextareaMinRows}
                            fullWidth
                        />

                        <UncontrolledTextField
                            name="unsubscribe-reason"
                            label={t("common:label.reason")}
                            placeholder={t("pages:adminAnnouncementsPage.unsubscribe.reason.hint")}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            helperText={t("pages:adminAnnouncementsPage.unsubscribe.reason.counter", {
                                current: reason.length,
                                max: reasonMaxLength,
                            })}
                            slotProps={{ htmlInput: { maxLength: reasonMaxLength } }}
                            fullWidth
                        />
                        <Box className={classes.formActions}>
                            <PrimaryButton
                                onClick={handleUnsubscribeClick}
                                loading={isUnsubscribing}
                                disabled={isUnsubscribing || !emailsText.trim()}
                            >
                                {t("pages:adminAnnouncementsPage.unsubscribe.submit")}
                            </PrimaryButton>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Typography variant="body1" className={classes.countText}>
                {t("pages:adminAnnouncementsPage.totalCount", { count: totalCount })}
            </Typography>

            <Box className={classes.toolbar}>
                <Box className={classes.searchContainer}>
                    <QueryBuilderField
                        placeholder={t("common:button.search")}
                        onChange={(value) => handleEmailSearch(value)}
                        onClear={() => handleEmailSearch("")}
                    />
                </Box>
                <Box className={classes.toolbarActions}>
                    <DropdownContainer
                        label={t("pages:adminAnnouncementsPage.export.label")}
                        icon={<i className="fa-solid fa-download" />}
                        anchorRef={exportAnchorRef}
                        disabled={isLoading || isExporting}
                        triggerProps={{ buttonType: "primary" }}
                    >
                        <DropdownItem
                            label={t("pages:adminAnnouncementsPage.export.subscribers")}
                            iconName="fa-solid fa-list"
                            onClick={handleExportSubscribers}
                        />
                        <DropdownItem
                            label={t("pages:adminAnnouncementsPage.export.customers")}
                            iconName="fa-solid fa-users"
                            onClick={handleExportCustomers}
                        />
                    </DropdownContainer>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>

                    <TableBody
                        table={table}
                        loading={isLoading}
                        noDataMessage={emptyMessage}
                        filter={{ isActive: !!emailFilter.length, onResetFilter: () => handleEmailSearch("") }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <HoverRow
                                key={row.id}
                                {...row}
                                selected={isEmailInTextarea(emailsText, row.original.mail)}
                                onClick={() => handleToggleRow(row.original.mail)}
                            >
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>

            <CustomTablePagination
                component="div"
                showFirstButton
                showLastButton
                rowsPerPageOptions={[10, 25, 50]}
                count={totalCount}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => setPageIndex(page)}
                onRowsPerPageChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPageIndex(0);
                }}
            />

            <ConfirmDialog
                title={t("pages:adminAnnouncementsPage.unsubscribe.confirm.title")}
                dangerButton
                primaryActionText={t("pages:adminAnnouncementsPage.unsubscribe.submit")}
                open={confirmOpen}
                onConfirm={handleConfirmUnsubscribe}
                onClose={() => setConfirmOpen(false)}
            >
                <DialogContentText color="text.primary">
                    {t("pages:adminAnnouncementsPage.unsubscribe.confirm.message", { count: pendingEmails.length })}
                </DialogContentText>
            </ConfirmDialog>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminAnnouncementsPage, {
        title: i18next.t("pages:adminAnnouncementsPage.metaTitle"),
        description: i18next.t("pages:adminAnnouncementsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole, EmteriaRoles.EmteriaSalesRole],
);
